import { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const steps = [
  {
    logo: true,
    title: 'Create Art Together',
    description: 'Art Ping Pong brings your family and friends together through collaborative art. Take turns creating, drawing, and building on each other\'s work.',
  },
  {
    rossAvatar: true,
    title: 'How It Works',
    description: 'Ross (your AI art teacher) can help as you create. Draw and/or use AI to create art. Pass it along. Watch your group masterpiece grow!',
    steps: ['Draw something or ask Ross to help', 'Draw on it with your own touch', 'Pass it to the next person'],
  },
  {
    emoji: '🚀',
    title: "Let's do this!",
    description: "Create or join a circle of friends/family and start making art together. It's that simple.",
  },
];

export default function OnboardingPage() {
  const [step, setStep] = useState(0);

  const finish = () => {
    localStorage.setItem('app_onboarding_completed', 'true');
    window.location.reload();
  };

  const isLast = step === steps.length - 1;
  const current = steps[step];

  return (
    <Container>
      <SkipButton onClick={finish}>Skip</SkipButton>

      <AnimatePresence mode="wait">
        <StepContent
          key={step}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.3 }}
        >
          {current.logo ? (
            <LogoImg src="/AppLogoIcon.svg" alt="Art Ping Pong" />
          ) : current.rossAvatar ? (
            <RossImg src="/ross.png" alt="Ross, your AI art teacher" />
          ) : (
            <Emoji>{current.emoji}</Emoji>
          )}
          <Title>{current.title}</Title>
          <Description>{current.description}</Description>
          {current.steps && (
            <StepList>
              {current.steps.map((s, i) => (
                <StepItem key={i}>
                  <StepNumber>{i + 1}</StepNumber>
                  <span>{s}</span>
                </StepItem>
              ))}
            </StepList>
          )}
        </StepContent>
      </AnimatePresence>

      <Bottom>
        <Dots>
          {steps.map((_, i) => (
            <Dot key={i} $active={i === step} />
          ))}
        </Dots>
        <CTA onClick={isLast ? finish : () => setStep(step + 1)}>
          {isLast ? "Let's Go!" : 'Next'}
        </CTA>
        <LegalFooter>
          <LegalLink href="https://app.domoreworld.com/privacy" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </LegalLink>
          <LegalSeparator>•</LegalSeparator>
          <LegalLink href="https://app.domoreworld.com/terms" target="_blank" rel="noopener noreferrer">
            Terms of Service
          </LegalLink>
        </LegalFooter>
      </Bottom>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100dvh;
  padding: calc(24px + env(safe-area-inset-top, 0px)) calc(24px + env(safe-area-inset-right, 0px)) 24px calc(24px + env(safe-area-inset-left, 0px));
  background: linear-gradient(180deg, #fffbeb 0%, #ffffff 40%);
  position: relative;
`;

const SkipButton = styled.button`
  position: absolute;
  top: 16px;
  right: 20px;
  font-size: 15px;
  color: ${({ theme }) => theme.colors.textTertiary};
  padding: 8px 12px;
`;

const StepContent = styled(motion.div)`
  text-align: center;
  max-width: 360px;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const LogoImg = styled.img`
  height: 100px;
  width: auto;
  margin-bottom: 24px;
`;

const RossImg = styled.img`
  height: 100px;
  width: 100px;
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: 24px;
`;

const Emoji = styled.div`
  font-size: 72px;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 12px;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Description = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const StepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
  text-align: left;
`;

const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 15px;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const StepNumber = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.accent};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
`;

const Bottom = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding-bottom: 24px;
`;

const Dots = styled.div`
  display: flex;
  gap: 8px;
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $active, theme }) => ($active ? theme.colors.accent : theme.colors.border)};
  transition: background 0.3s;
`;

const CTA = styled.button`
  width: 280px;
  padding: 16px;
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.accent};
  color: white;
  font-size: 17px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
  transition: all 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(245, 158, 11, 0.4);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const LegalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 11px;
  opacity: 0.7;
`;

const LegalLink = styled.a`
  color: ${({ theme }) => theme.colors.textSecondary};
  text-decoration: none;
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`;

const LegalSeparator = styled.span`
  color: ${({ theme }) => theme.colors.textTertiary};
`;
